import { type ReactElement, useEffect, useState } from 'react'
import { useRequiredParams } from '@util'
import { type ImportId, isImportInProgress, SECOND } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Divider, Grid, Stack } from '@mantine/core'
import { Size, useImport, useTranslation } from '@front/model'
import { NotReady, Page, TAuditInfoSection, TListItem, TSectionList, UserInfo } from '@components'
import { ImportStatusChip } from './StatusChip'
import { ImportStatusMessage } from './ImportStatusMessage'
import { shouldPollImport } from './importStatus'
import { ImportSettings } from './ImportSettings'
import { ImportResults } from './ImportResults'
import { ImportCleanup } from './ImportCleanup'

const POLL_INTERVAL = 2 * SECOND

function ImportPage(): ReactElement {
  const { t } = useTranslation()
  const { importId } = useRequiredParams<{ importId: ImportId }>()

  const [shouldPoll, setShouldPoll] = useState(true)
  const { data, isLoading, error, refetch, isFetching } = useImport(importId, {
    refetchInterval: shouldPoll ? POLL_INTERVAL : undefined,
  })
  useEffect(() => {
    if (!data) return
    setShouldPoll(shouldPollImport(data))
  }, [data])

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Import' isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading back isFetching={isFetching} refetch={refetch} title={t('Import Details')}>
        {data && <ImportStatusChip status={data.importStatus} />}
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='lg'>
          <ImportStatusMessage importData={data} />
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <ImportResults importData={data} />
            </Grid.Col>
            <Divider mx={Size.space['3xl']} orientation='vertical' />
            <Grid.Col span={{ base: 12, lg: 'auto' }}>
              <Stack>
                <ImportSettings importData={data} />
                <TSectionList heading={{ title: t('Info') }}>
                  {data.name && <TListItem detailBold details={data.name} label={t('Name')} />}
                  <TListItem detailBold details={<UserInfo userId={data.ownerId} />} label={t('Owner')} />
                  {!data.isDeleted && data.entity !== 'User' && !isImportInProgress(data.importStatus) && (
                    <ImportCleanup data={data} />
                  )}
                </TSectionList>
                <TAuditInfoSection {...data}>
                  <TListItem canCopy detailBold details={data.importId} label={t('Import ID')} />
                </TAuditInfoSection>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/integration/import_/$importId/')({
  component: ImportPage,
})
